import React, { useState, useRef } from 'react';
import { Settings, Key, Bell, AlertTriangle, Eye, EyeOff, Check, X, Copy, RefreshCw, Upload, Building } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

function ToggleCard({ label, desc, checked, onChange }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.04)' }}>
      <div>
        <p className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{label}</p>
        <p className="text-xs mt-0.5" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{desc}</p>
      </div>
      <button
        onClick={onChange}
        className="relative inline-flex items-center w-10 h-5 rounded-full transition-all duration-200 flex-shrink-0 ml-4"
        style={{ background: checked ? '#10B981' : 'rgba(100,116,139,0.3)' }}
      >
        <span className="inline-block w-4 h-4 rounded-full bg-white transition-transform duration-200" style={{ transform: checked ? 'translateX(22px)' : 'translateX(2px)' }} />
      </button>
    </div>
  );
}

export default function PartnerSettings({ partnerProfile, onProfileUpdate }) {
  const [form, setForm] = useState({
    business_name: partnerProfile?.business_name || '',
    contact_email: partnerProfile?.contact_email || '',
    website_url: partnerProfile?.website_url || '',
    logo_url: partnerProfile?.logo_url || '',
  });
  const [notifications, setNotifications] = useState({
    notify_scan_milestones: partnerProfile?.notify_scan_milestones ?? true,
    notify_weekly_digest: partnerProfile?.notify_weekly_digest ?? true,
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingNotifs, setSavingNotifs] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const [deactivateConfirm, setDeactivateConfirm] = useState(false);
  const [deactivateInput, setDeactivateInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [currentApiKey, setCurrentApiKey] = useState(partnerProfile?.api_key || '');
  const logoInputRef = useRef(null);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  const handleSaveProfile = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { error } = await supabase?.from('partner_profiles')?.update(form)?.eq('id', partnerProfile?.id);
      if (error) { showMsg('error', error?.message); return; }
      showMsg('success', 'Profile updated successfully!');
      onProfileUpdate?.({ ...partnerProfile, ...form });
    } catch (e) { showMsg('error', 'Failed to save changes'); } finally { setSaving(false); }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifs(true);
    try {
      const { error } = await supabase?.from('partner_profiles')?.update(notifications)?.eq('id', partnerProfile?.id);
      if (error) { showMsg('error', error?.message); return; }
      showMsg('success', 'Notification preferences saved!');
      onProfileUpdate?.({ ...partnerProfile, ...notifications });
    } catch (e) { showMsg('error', 'Failed to save preferences'); } finally { setSavingNotifs(false); }
  };

  const handleCopyKey = () => {
    navigator?.clipboard?.writeText(currentApiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRegenKey = async () => {
    if (!confirm('Regenerate API key? Your existing integrations will stop working until updated.')) return;
    setRegenLoading(true);
    try {
      const newKey = Array.from(crypto.getRandomValues(new Uint8Array(32)))?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
      const { error } = await supabase?.from('partner_profiles')?.update({ api_key: newKey })?.eq('id', partnerProfile?.id);
      if (error) { showMsg('error', error?.message); return; }
      setCurrentApiKey(newKey);
      showMsg('success', 'API key regenerated!');
    } catch (e) { showMsg('error', 'Failed to regenerate key'); } finally { setRegenLoading(false); }
  };

  const handleLogoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const ext = file?.name?.split('.')?.pop();
      const path = `partner-logos/${partnerProfile?.id}.${ext}`;
      const { error: uploadErr } = await supabase?.storage?.from('partner-assets')?.upload(path, file, { upsert: true });
      if (uploadErr) {
        // Fallback: use object URL for preview
        const url = URL.createObjectURL(file);
        setForm(f => ({ ...f, logo_url: url }));
        showMsg('info', 'Logo preview set. Save profile to apply.');
        return;
      }
      const { data: { publicUrl } } = supabase?.storage?.from('partner-assets')?.getPublicUrl(path);
      setForm(f => ({ ...f, logo_url: publicUrl }));
      await supabase?.from('partner_profiles')?.update({ logo_url: publicUrl })?.eq('id', partnerProfile?.id);
      showMsg('success', 'Logo uploaded successfully!');
    } catch (e) { showMsg('error', 'Failed to upload logo'); } finally { setLogoUploading(false); }
    e.target.value = '';
  };

  const handleDeactivate = async () => {
    if (deactivateInput !== (partnerProfile?.business_name || '')) {
      showMsg('error', 'Company name does not match');
      return;
    }
    try {
      await supabase?.from('partner_profiles')?.update({ is_active: false })?.eq('id', partnerProfile?.id);
      await supabase?.auth?.signOut();
    } catch (e) { console.error(e); }
  };

  const maskedKey = currentApiKey ? `sk-${currentApiKey?.substring(0, 4)}••••••••••••••••••••${currentApiKey?.slice(-4)}` : 'No API key generated';
  const inputStyle = { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Settings</h2>
        <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Manage your partner account preferences</p>
      </div>

      {msg?.text && (
        <div className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" style={{ background: msg?.type === 'success' ? 'rgba(16,185,129,0.1)' : msg?.type === 'info' ? 'rgba(0,229,255,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${msg?.type === 'success' ? 'rgba(16,185,129,0.2)' : msg?.type === 'info' ? 'rgba(0,229,255,0.2)' : 'rgba(239,68,68,0.2)'}`, color: msg?.type === 'success' ? '#10B981' : msg?.type === 'info' ? '#00E5FF' : '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>
          {msg?.type === 'success' ? <Check size={14} /> : <X size={14} />}
          {msg?.text}
          <button onClick={() => setMsg({ type: '', text: '' })} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Company Profile */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,229,255,0.08)' }}>
        <div className="flex items-center gap-2 mb-5">
          <Building size={16} style={{ color: '#00E5FF' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Company Profile</h3>
        </div>

        {/* Logo Upload */}
        <div className="flex items-center gap-4 mb-5 p-4 rounded-xl" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(0,229,255,0.08)' }}>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.1), rgba(123,47,190,0.1))', border: '1px solid rgba(0,229,255,0.15)' }}>
            {form?.logo_url ? (
              <img src={form?.logo_url} alt="Company logo" className="w-full h-full object-cover" />
            ) : (
              <span className="text-2xl font-bold text-white">{(form?.business_name || 'P')?.charAt(0)?.toUpperCase()}</span>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-white mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>Company Logo</p>
            <p className="text-xs mb-2" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>PNG, JPG up to 2MB</p>
            <button
              onClick={() => logoInputRef?.current?.click()}
              disabled={logoUploading}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium"
              style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.15)', color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}
            >
              <Upload size={12} />
              {logoUploading ? 'Uploading...' : 'Upload Logo'}
            </button>
            <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
          </div>
        </div>

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Business Name</label>
              <input value={form?.business_name} onChange={e => setForm(f => ({ ...f, business_name: e?.target?.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Contact Email</label>
              <input type="email" value={form?.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e?.target?.value }))}
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Website URL</label>
              <input type="url" value={form?.website_url} onChange={e => setForm(f => ({ ...f, website_url: e?.target?.value }))}
                placeholder="https://yourcompany.com"
                className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none" style={inputStyle} />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.15)' }}>
              <span className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Partner Tier:</span>
              <span className="text-xs font-semibold capitalize" style={{ color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>{partnerProfile?.partner_tier || 'basic'}</span>
            </div>
            <span className="text-xs" style={{ color: '#475569', fontFamily: 'DM Sans, sans-serif' }}>(Read-only — contact support to upgrade)</span>
          </div>
          <button type="submit" disabled={saving} className="px-6 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* API Key */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(123,47,190,0.15)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Key size={16} style={{ color: '#7B2FBE' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>API Integration</h3>
        </div>
        <p className="text-xs mb-3" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Use this key to integrate with the FinVisAR Retail API</p>
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl mb-3" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(123,47,190,0.2)' }}>
          <code className="flex-1 text-sm" style={{ color: '#94A3B8', fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>
            {showApiKey ? currentApiKey : maskedKey}
          </code>
          <button onClick={() => setShowApiKey(s => !s)} className="flex-shrink-0" style={{ color: '#64748B' }}>
            {showApiKey ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleCopyKey}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
            style={{ background: copied ? 'rgba(16,185,129,0.1)' : 'rgba(0,229,255,0.08)', border: `1px solid ${copied ? 'rgba(16,185,129,0.2)' : 'rgba(0,229,255,0.15)'}`, color: copied ? '#10B981' : '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied!' : 'Copy Key'}
          </button>
          <button
            onClick={handleRegenKey}
            disabled={regenLoading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium"
            style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.15)', color: '#F59E0B', fontFamily: 'DM Sans, sans-serif' }}
          >
            <RefreshCw size={12} className={regenLoading ? 'animate-spin' : ''} />
            {regenLoading ? 'Regenerating...' : 'Regenerate Key'}
          </button>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(16,185,129,0.1)' }}>
        <div className="flex items-center gap-2 mb-4">
          <Bell size={16} style={{ color: '#10B981' }} />
          <h3 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Notification Preferences</h3>
        </div>
        <div className="space-y-3">
          <ToggleCard
            label="Scan Milestone Alerts"
            desc="Get notified when products reach 100, 500, or 1000 scans"
            checked={notifications?.notify_scan_milestones}
            onChange={() => setNotifications(n => ({ ...n, notify_scan_milestones: !n?.notify_scan_milestones }))}
          />
          <ToggleCard
            label="Weekly Analytics Digest"
            desc="Receive a weekly summary of your catalog performance"
            checked={notifications?.notify_weekly_digest}
            onChange={() => setNotifications(n => ({ ...n, notify_weekly_digest: !n?.notify_weekly_digest }))}
          />
        </div>
        <button
          onClick={handleSaveNotifications}
          disabled={savingNotifs}
          className="mt-4 px-4 py-2 rounded-xl text-xs font-medium"
          style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)', color: '#10B981', fontFamily: 'DM Sans, sans-serif' }}
        >
          {savingNotifs ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>

      {/* Danger Zone */}
      <div className="rounded-2xl p-5" style={{ background: 'rgba(239,68,68,0.03)', border: '1px solid rgba(239,68,68,0.15)' }}>
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle size={16} style={{ color: '#EF4444' }} />
          <h3 className="text-sm font-semibold" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>Danger Zone</h3>
        </div>
        {!deactivateConfirm ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Deactivate Partner Account</p>
              <p className="text-xs mt-0.5" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>This will permanently disable your partner portal access</p>
            </div>
            <button
              onClick={() => setDeactivateConfirm(true)}
              className="px-4 py-2 rounded-xl text-xs font-medium flex-shrink-0"
              style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}
            >
              Deactivate
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>Type your company name to confirm deactivation:</p>
            <p className="text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Company name: <strong style={{ color: '#E2E8F0' }}>{partnerProfile?.business_name}</strong></p>
            <input
              value={deactivateInput}
              onChange={e => setDeactivateInput(e?.target?.value)}
              placeholder="Type company name to confirm"
              className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
              style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', fontFamily: 'DM Sans, sans-serif' }}
            />
            <div className="flex gap-2">
              <button onClick={() => { setDeactivateConfirm(false); setDeactivateInput(''); }} className="px-4 py-2 rounded-xl text-xs" style={{ background: 'rgba(255,255,255,0.05)', color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
              <button
                onClick={handleDeactivate}
                disabled={deactivateInput !== partnerProfile?.business_name}
                className="px-4 py-2 rounded-xl text-xs font-semibold text-white"
                style={{ background: deactivateInput === partnerProfile?.business_name ? '#EF4444' : 'rgba(239,68,68,0.3)', fontFamily: 'DM Sans, sans-serif' }}
              >
                Confirm Deactivation
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
